import { useCallback, useReducer } from "react";
import { invoiceParseJobs } from "api/agentJobs";
import { useAgentJobRun } from "hooks/useAgentJobRun";
import { useTranslation } from "react-i18next";
import { InvoiceParsedRow, InvoiceParseJobDto } from "types/api/invoiceParseJobs";
import { pollAgentJob } from "utils/agentJob";
import {
  AgentEvent,
  AgentPhase,
  initialAgentPhase,
  reduceAgentPhase,
} from "utils/agentJobPhase";
import { PHOTO_MAX_BYTES } from "utils/photoUpload";
import {
  toastResponseError,
  toastResponseSuccess,
} from "utils/responseMessageHelper";

type Phase = AgentPhase<InvoiceParseJobDto>;
type Event = AgentEvent<InvoiceParseJobDto>;
const TERMINAL_STATUSES = ["completed", "failed"] as const;

const reduce = (phase: Phase, event: Event): Phase =>
  reduceAgentPhase(phase, event);

// Uploads a receipt (photo or PDF; the drop zone has checked the format) as an
// invoice_parse job, polls it, and hands the rows to the form. The phase drives
// the small status UI under the drop zone.
export const useInvoiceParse = ({
  onRows,
}: {
  onRows: (rows: InvoiceParsedRow[]) => void;
}) => {
  const { t } = useTranslation("common");
  const [phase, dispatch] = useReducer(reduce, initialAgentPhase as Phase);
  const { start: startRun, keepWaiting: resumePoll } = useAgentJobRun<
    InvoiceParseJobDto,
    "completed" | "failed"
  >({
    poll: (jobId, signal) =>
      pollAgentJob(jobId, {
        jobsApi: invoiceParseJobs,
        terminalStatuses: TERMINAL_STATUSES,
        signal,
      }),
    dispatch,
    onDone: (job) => {
      const rows = job.rows ?? [];
      dispatch({ type: "JOB_DONE", result: job });
      onRows(rows);
      toastResponseSuccess(t("invoiceParse.added", { count: rows.length }));
      // Rows are in the form now; the drop zone is ready for the next receipt.
      dispatch({ type: "DISCARDED" });
    },
  });

  const start = useCallback(
    (file: File) => {
      // Only a receipt that is being read right now blocks the next one; a
      // receipt dropped under a failure notice or a stuck job starts over.
      if (phase.name === "parsing" || phase.name === "review") return;
      if (file.size > PHOTO_MAX_BYTES) {
        toastResponseError(t("invoiceParse.tooLarge"));
        return;
      }
      if (phase.name === "failed") dispatch({ type: "RETRY" });
      dispatch({ type: "SUBMIT", canSubmit: true });
      const body = new FormData();
      body.append("image", file);
      startRun(() => invoiceParseJobs.create(body));
    },
    [phase.name, startRun, t],
  );

  const keepWaiting = useCallback(() => {
    if (phase.name !== "timeout") return;
    resumePoll(phase.jobId);
  }, [phase, resumePoll]);

  const retry = useCallback(() => dispatch({ type: "RETRY" }), []);

  return { phase, start, keepWaiting, retry };
};
